/* begin general import */
import { openOnRowClick } from "helpers/row-click";
import { Button, Card, Col, Dropdown, Menu, Row, message } from "antd";
import Table, { ColumnProps } from "antd/lib/table";
import classNames from "classnames";
import InputSearch from "components/Utility/InputSearch/InputSearch";
import Pagination from "components/Utility/Pagination/Pagination";
import React, { useMemo } from "react";
import { useTranslation } from "react-i18next";
import { CSSTransition } from "react-transition-group";
import masterService from "services/pages/master-service";
import { getAntOrderType } from "services/table-service";
import nameof from "ts-nameof.macro";
import "./HomeControllerMaster.scss";
/* end general import */

/* begin filter import */
import { IdFilter, StringFilter } from "@react3l/advanced-filters";
import AdvanceStringFilter from "components/Utility/AdvanceFilter/AdvanceStringFilter/AdvanceStringFilter";
/* end filter import */

/* begin individual import */
import AdvanceIdFilter from "components/Utility/AdvanceFilter/AdvanceIdFilter/AdvanceIdFilter";
import { HOME_CONTROLLER_ROUTE } from "config/route-consts";
import { formatDate } from "helpers/date-time";
import { Dormitory } from "models/Dormitory";
import { HomeController, HomeControllerFilter } from "models/HomeController";
import { Status, StatusFilter } from "models/Status";
import { Moment } from "moment";
import { homeControllerRepository } from "repositories/home-controller-repository";
// [Review] service riêng của menu đặt vào trong folder của menu luôn, không đặt vào services
import SendNotificationModal from "components/Molecules/NotificationForm/NotificationForm";
import { SendNotificationTypeEnum } from "config/consts";
import { UserRoleEnum } from "config/enum";
import { authService } from "services/auth-services";
import { sendNotificationServive } from "services/send-notification-service";
/* end individual import */

function HomeControllerMaster() {
  const [translate] = useTranslation();
  const [showForm, setShowForm] = React.useState(false);

  const { isRoleSupportFeature } = authService.useRole();

  const {
    list,
    total,
    loadingList,
    filter,
    toggle,
    handleChangeFilter,
    handleUpdateNewFilter,
    handleToggleSearch,
    handleTableChange,
    handlePagination,
    handleImportList,
    handleGoDetail,
    rowSelection,
    importButtonRef
  } = masterService.useMaster<HomeController, HomeControllerFilter>(
    HomeControllerFilter,
    HOME_CONTROLLER_ROUTE,
    homeControllerRepository.list,
    homeControllerRepository.count
  );
  const {
    notification,
    handleChangeTitle,
    handleChangeContent,
    handleChangeIds,
    handleChangeImageUrl,
    handleChangeLink,
    handleSend
  } = sendNotificationServive.useNotificationForm(SendNotificationTypeEnum.HomeController);

  const handleConfirm = React.useCallback(() => {
    handleSend().then((response) => {
      if (response) {
        setShowForm(false);
      }
    });
  }, [handleSend]);
  const handleCancelModal = React.useCallback(() => {
    setShowForm(false);
  }, []);
  const handleShowModal = React.useCallback(() => {
    setShowForm(true);
  }, []);
  const _handleChangeFilter = React.useCallback(
    (value) => {
      const newFilter = { ...filter };
      newFilter["search"] = value;
      handleUpdateNewFilter(newFilter);
    },
    [filter, handleUpdateNewFilter]
  );

  const handleExportClick = React.useCallback(() => {
    message.info("Tính năng đang phát triển");
  }, []);

  const handleImportClick = React.useCallback(() => {
    importButtonRef.current.click();
  }, [importButtonRef]);

  const menuAction = React.useCallback(
    (id: number) => (
      <Menu>
        <Menu.Item key="1">
          <div className="ant-action-menu" onClick={handleGoDetail(id)}>
            {translate("general.actions.view")}
          </div>
        </Menu.Item>
      </Menu>
    ),
    [handleGoDetail, translate]
  );

  // Thứ tự cột theo việc chính của màn: nhận ra HC, xem có online không, rồi mới tới thông tin phụ.
  const columns: ColumnProps<HomeController>[] = useMemo(
    () => [
      {
        title: "Home Controller",
        key: nameof(list[0].name),
        dataIndex: nameof(list[0].name),
        sorter: false,
        sortOrder: getAntOrderType<HomeController, HomeControllerFilter>(
          filter,
          nameof(list[0].name)
        ),
        render(name: string, hc: HomeController) {
          return (
            <div className="ant-cell-master__container">
              <div className={classNames("cell-master__first-row")}>{name}</div>
              <div className={classNames("cell-master__second-row")}>
                {hc.macAddress || "—"}
              </div>
            </div>
          );
        },
      },

      {
        title: translate("homeController.status"),
        key: nameof(list[0].homeControllerActiveStatus),
        dataIndex: nameof(list[0].homeControllerActiveStatus),
        sorter: true,
        sortOrder: getAntOrderType<HomeController, HomeControllerFilter>(
          filter,
          nameof(list[0].homeControllerActiveStatus)
        ),
        width: 140,
        render(hcStatus: Status) {
          if (!hcStatus) return <span className="text-muted-token">—</span>;
          return (
            <span
              className={`home-controller__status home-controller__status--${hcStatus.id}`}
            >
              {hcStatus.name}
            </span>
          );
        },
      },

      {
        title: translate("homeController.dormitory"),
        key: nameof(list[0].dormitory),
        dataIndex: nameof(list[0].dormitory),
        sorter: false,
        sortOrder: getAntOrderType<HomeController, HomeControllerFilter>(
          filter,
          nameof(list[0].dormitory)
        ),
        ellipsis: true,
        render(dormitory: Dormitory) {
          return dormitory?.name || <span className="text-muted-token">—</span>;
        },
      },

      {
        title: "Địa chỉ IP",
        key: nameof(list[0].ipAddress),
        dataIndex: nameof(list[0].ipAddress),
        sorter: false,
        sortOrder: getAntOrderType<HomeController, HomeControllerFilter>(
          filter,
          nameof(list[0].ipAddress)
        ),
        render(ipAddress: string) {
          return (
            <span className="tabular-nums">
              {ipAddress || <span className="text-muted-token">—</span>}
            </span>
          );
        },
      },

      {
        title: "Phiên bản FW",
        key: nameof(list[0].version),
        dataIndex: nameof(list[0].version),
        sorter: true,
        sortOrder: getAntOrderType<HomeController, HomeControllerFilter>(
          filter,
          nameof(list[0].version)
        ),
        width: 140,
        render(version: string) {
          return (
            <span className="tabular-nums">
              {version || <span className="text-muted-token">—</span>}
            </span>
          );
        },
      },

      {
        title: "Ngày tạo",
        key: nameof(list[0].createdAt),
        dataIndex: nameof(list[0].createdAt),
        sorter: true,
        sortOrder: getAntOrderType<HomeController, HomeControllerFilter>(
          filter,
          nameof(list[0].createdAt)
        ),
        width: 130,
        render(createdAt: Moment) {
          return <span className="tabular-nums">{formatDate(createdAt)}</span>;
        },
      },

      {
        title: <span className="sr-only">{translate("general.actions.action")}</span>,
        key: "action",
        dataIndex: nameof(list[0].id),
        fixed: "right",
        width: 56,
        align: "right",
        render(id: number) {
          return (
            <Dropdown
              overlay={menuAction(id)}
              trigger={["click"]}
              placement="bottomRight"
            >
              <button
                type="button"
                className="action__dots"
                aria-label={translate("general.actions.action") as string}
              >
                <i className="tio-more_horizontal" />
              </button>
            </Dropdown>
          );
        },
      },
    ],
    [translate, list, filter, menuAction]
  );

  const filterChildren = useMemo(
    () => (
      <Row className="mt-4">
        <Col lg={4} className="pr-4">
          <label className="label">{translate("homeController.name")}</label>
          <AdvanceStringFilter
            isMaterial={true}
            value={filter[nameof(list[0].name)]["contain"]}
            onEnter={handleChangeFilter(
              nameof(list[0].name),
              "contain" as any,
              StringFilter
            )}
            placeHolder="Tìm kiếm..."
          />
        </Col>

        <Col lg={4} className="pr-4">
          <label className="label">
            {translate("homeController.macAddress")}
          </label>
          <AdvanceStringFilter
            isMaterial={true}
            value={filter[nameof(list[0].macAddress)]["contain"]}
            onEnter={handleChangeFilter(
              nameof(list[0].macAddress),
              "contain" as any,
              StringFilter
            )}
            placeHolder="Tìm kiếm..."
          />
        </Col>

        <Col lg={4} className="pr-4">
          <label className="label">
            {translate("homeController.ipAddress")}
          </label>
          <AdvanceStringFilter
            isMaterial={true}
            value={filter[nameof(list[0].ipAddress)]["contain"]}
            onEnter={handleChangeFilter(
              nameof(list[0].ipAddress),
              "contain" as any,
              StringFilter
            )}
            placeHolder="Tìm kiếm..."
          />
        </Col>

        <Col lg={4} className="pr-4">
          <label className="label">{translate("homeController.status")}</label>
          <AdvanceIdFilter
            onChange={handleChangeFilter(
              nameof(list[0].homeControllerActiveStatusId),
              "equal" as any,
              IdFilter
            )}
            value={
              filter[nameof(list[0].homeControllerActiveStatusId)]["equal"]
            }
            isMaterial={true}
            classFilter={StatusFilter}
            isEnumList={true}
            getList={
              homeControllerRepository.filterListHomeControllerActiveStatus
            }
            placeHolder="Tất cả"
          />
        </Col>

        <Col lg={4} className="pr-4">
          <label className="label">{translate("homeController.version")}</label>
          <AdvanceStringFilter
            isMaterial={true}
            value={filter[nameof(list[0].version)]["contain"]}
            onEnter={handleChangeFilter(
              nameof(list[0].version),
              "contain" as any,
              StringFilter
            )}
            placeHolder="Tìm kiếm..."
          />
        </Col>
      </Row>
    ),
    [filter, handleChangeFilter, list, translate]
  );

  return (
    <>
      <div className="page page__master home-controller__container">
        <div className="page__header d-flex align-items-center justify-content-between">
          <div className="page__title">
            {translate("homeController.master.title")}
          </div>
        </div>
        <div className="page__search">
          <Card bordered={false}>
            <div className="d-flex align-items-center justify-content-between">
              <div className="d-flex w-50">
                <div className="w70">
                  <InputSearch
                    value={filter["search"]}
                    onChange={_handleChangeFilter}
                    placeHolder="Tìm kiếm"
                  />
                </div>
                <button
                  className={classNames(
                    "btn component__btn-toggle ml-2",
                    toggle === true ? "component__btn-toggle-active" : ""
                  )}
                  onClick={handleToggleSearch}
                >
                  <i className="tio-tune_horizontal mr-2"></i>
                  <span className="component_btn-text">
                    {translate("general.button.advance")}
                  </span>
                </button>
              </div>
              <div className="d-flex align-items-center">
                {isRoleSupportFeature([UserRoleEnum.Admin, UserRoleEnum.Operate]) && (
                  <button
                    className="btn component__btn-primary mr-3"
                    onClick={handleShowModal}
                  >
                    <i className="tio-send_outlined mr-2"></i>
                    <span className="component_btn-text">Gửi thông báo</span>
                  </button>
                )}
                <div className="d-flex" style={{ gap: "8px" }}>
                  <Button onClick={handleExportClick}>
                    <i className="tio-download_to mr-2"></i>
                    <span className="component_btn-text">Xuất dữ liệu</span>
                  </Button>
                  <Button onClick={handleImportClick}>
                    <i className="tio-file_add_outlined mr-2"></i>
                    <span className="component_btn-text">{translate("general.button.importExcel")}</span>
                  </Button>
                  <input
                    ref={importButtonRef}
                    type="file"
                    style={{ display: "none" }}
                    id="master-import"
                    onChange={handleImportList(homeControllerRepository.import)}
                  />
                </div>
              </div>
            </div>
            <CSSTransition
              in={toggle}
              timeout={100}
              classNames={"show"}
              unmountOnExit
            >
              {filterChildren}
            </CSSTransition>
          </Card>
        </div>
        <div className="page__master-table custom-scrollbar pb-4">
          <Card bordered={false}>
            <Table
              onRow={(record) => openOnRowClick(handleGoDetail(record.id))}
              rowKey={nameof(list[0].id)}
              columns={columns}
              pagination={false}
              dataSource={list}
              loading={loadingList}
              rowSelection={{
                onChange(selectedRowKeys: any) {
                  rowSelection.onChange(selectedRowKeys);
                  handleChangeIds(selectedRowKeys);
                },
                type: 'checkbox'
              }}
              onChange={handleTableChange}
              scroll={{ x: "max-content" }}
              title={() => (
                <>
                  <div className="d-flex justify-content-between align-items-center">
                    <span className="action__result">{total} Kết quả</span>
                    <div className="flex-shrink-1 d-flex align-items-center">
                      <Pagination
                        skip={filter.skip}
                        take={filter.take}
                        total={total}
                        onChange={handlePagination}
                        style={{ margin: "10px" }}
                      />
                    </div>
                  </div>
                </>
              )}
            />
          </Card>
        </div>
        <SendNotificationModal
          isOpen={showForm}
          notification={notification}
          confirm={handleConfirm}
          cancel={handleCancelModal}
          onChangeTitle={handleChangeTitle}
          onChangeContent={handleChangeContent}
          onChangeImage={handleChangeImageUrl}
          onChangeLink={handleChangeLink}
        />
      </div>
    </>
  );
}

export default HomeControllerMaster;
